import './styles.scoped.css'
import {Typography, Button, IconButton, Avatar, Chip, Stepper, Step, StepLabel, Skeleton, Modal, TextField} from '@mui/material'
import MuiStepIcon from '@mui/material/StepIcon'
import {ArrowBack, Timelapse, Check, Cancel, Close, RemoveCircle, Warning} from '@mui/icons-material'
import {Link, useParams} from 'react-router-dom'
import {useState, useEffect} from 'react'
import {useMobile} from '../../hooks'
import {getInitials, getColorFromName, formatTime, parseError} from '../../utils/helpers'
import {format} from 'date-fns'
import {evvApi, scheduleApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {VisitWindow, ScheduleVsActual, VisitInformation, ActivityLog} from './components'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {SkeletonContainer, TextSkeleton, ChipSkeleton, AvatarSkeleton, ButtonSkeleton} from '../../components'

const isoTime = iso => (iso ? format(new Date(iso), 'HH:mm') : null)

const STEPS = ['Pending review', 'Submitted to HHAX', 'Approved']

const STATUS_TO_STEP = {
    pending_review: 0,
    submitted: 1,
    rejected: 1,
    approved: 2,
    cancelled: 1,
    blocked: 1,
}

const STATUS_TO_ERROR_STEP = {
    rejected: 1,
    cancelled: 1,
    blocked: 1,
}

const STATUS_TO_ERROR_STYLE = {
    rejected: {icon: Cancel, color: '#D32F2F'},
    cancelled: {icon: Cancel, color: '#D32F2F'},
    blocked: {icon: RemoveCircle, color: '#7B1FA2'},
}

const makeStepIcon = errorStyle => props => {
    if (props.error && errorStyle) {
        const ErrorIcon = errorStyle.icon
        return <ErrorIcon style={{color: errorStyle.color, fontSize: 24}} />
    }
    return <MuiStepIcon {...props} />
}

const NO_STEPPER_STATUSES = ['not_required', 'withdrawn']

const WARNING_DETAILS = {
    late: {
        title: 'Late Clock-in',
        actionLabel: 'Add reason',
        description: ts =>
            ts?.is_late_clock_in && ts?.actual_start
                ? `Caregiver clocked in ${ts.late_minutes} minutes late (${formatTime(isoTime(ts.actual_start))} vs ${formatTime(ts.scheduled_start)} scheduled)`
                : 'Caregiver clocked in later than scheduled.',
    },
    early: {
        title: 'Early Clock-out',
        actionLabel: 'Add reason',
        description: ts =>
            ts?.is_early_clock_out && ts?.actual_end
                ? `Caregiver clocked out ${ts.early_minutes} minutes early (${formatTime(isoTime(ts.actual_end))} vs ${formatTime(ts.scheduled_end)} scheduled)`
                : 'Caregiver clocked out earlier than scheduled.',
    },
    outside_radius: {
        title: 'Location mismatch',
        actionLabel: 'Review',
        description: (ts, ls) => {
            const distance = ls?.clock_in_location_mismatch ? ls?.clock_in_distance_feet : ls?.clock_out_distance_feet
            return distance
                ? `Clock-in location is ${Math.round(distance)} ft from the registered service address`
                : 'Clock-in or clock-out location was far from the registered service address'
        },
    },
    missing_signature: {
        title: 'Missing signature',
        actionLabel: 'Review',
        description: () => 'A required signature is missing for this visit.',
    },
}

// `queued` splits the submitted tab into "still ours" and "HHAX has it". See the
// same chip in Evv/components/EvvVisits: a queued visit has been submitted to
// nothing, and must not tell an agency otherwise.
const statusChip = (value, queued) => {
    if (value === 'pending_review') {
        return <Chip label="Pending for Review" variant="filled" color="warning" />
    } else if (value === 'submitted') {
        return queued ? <Chip label="Queued" variant="outlined" color="info" /> : <Chip label="Submitted" variant="filled" color="info" />
    } else if (value === 'approved') {
        return <Chip label="Approved" variant="filled" color="success" />
    } else if (value === 'rejected') {
        return <Chip label="Rejected" variant="filled" color="error" />
    } else if (value === 'cancelled') {
        return <Chip label="Cancelled" variant="filled" color="error" />
    } else if (value === 'blocked') {
        return <Chip label="Blocked" variant="filled" color="error" />
    } else if (value === 'not_required') {
        return <Chip label="Not Required" variant="outlined" sx={{color: 'text.secondary', borderColor: 'divider'}} />
    } else if (value === 'withdrawn') {
        return (
            <Chip
                label="Withdrawn"
                variant="filled"
                sx={theme => ({backgroundColor: theme.palette.grey[300], color: theme.palette.text.secondary})}
            />
        )
    }
    return null
}

const formatDurationMinutes = minutes => `${Math.floor(minutes / 60)}h ${minutes % 60}m`

const toMinutes = time => {
    const [hour, minute] = time.split(':').map(Number)
    return hour * 60 + minute
}

const minutesToTime = minutes => {
    const wrapped = ((minutes % 1440) + 1440) % 1440
    return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`
}

const formatAddress = address => {
    if (!address) {
        return null
    }
    return `${address?.street_line1}${address?.street_line2 ? ', ' + address.street_line2 : ''}, ${address?.city}, ${address?.state}, ${address?.zip_code}`
}

const buildScheduleVsActual = visitInformation => {
    const ts = visitInformation?.visit_time_summary
    const ls = visitInformation?.visit_location_summary
    const actualStart = ts?.actual_start ? isoTime(ts.actual_start) : null
    const actualEnd = ts?.actual_end ? isoTime(ts.actual_end) : null
    const scheduledAddress = formatAddress(ls?.scheduled_address)
    const scheduledDuration = ts?.scheduled_start && ts?.scheduled_end ? toMinutes(ts.scheduled_end) - toMinutes(ts.scheduled_start) : null

    return [
        {
            label: 'Clock-in time',
            scheduled: ts?.scheduled_start ? formatTime(ts.scheduled_start) : '-',
            actual: actualStart ? formatTime(actualStart) : '-',
            actual_note: ts?.is_late_clock_in ? `${ts.late_minutes} min late` : undefined,
            differs: Boolean(actualStart) && actualStart !== ts?.scheduled_start,
        },
        {
            label: 'Clock-out time',
            scheduled: ts?.scheduled_end ? formatTime(ts.scheduled_end) : '-',
            actual: actualEnd ? formatTime(actualEnd) : '-',
            actual_note: ts?.is_early_clock_out ? `${ts.early_minutes} min early` : undefined,
            differs: Boolean(actualEnd) && actualEnd !== ts?.scheduled_end,
        },
        {
            label: 'Duration',
            scheduled: scheduledDuration != null ? formatDurationMinutes(scheduledDuration) : '-',
            actual: ts?.duration_minutes != null ? formatDurationMinutes(ts.duration_minutes) : '-',
            differs: ts?.duration_minutes != null && scheduledDuration != null && ts.duration_minutes !== scheduledDuration,
        },
        {
            label: 'Clock-in location',
            scheduled: scheduledAddress,
            actual: ls?.clock_in_address,
            actual_note: ls?.clock_in_location_mismatch ? `${Math.round(ls.clock_in_distance_feet)} ft from service address` : undefined,
            differs: Boolean(ls?.clock_in_address) && ls?.clock_in_address !== scheduledAddress,
            isLocation: true,
        },
        {
            label: 'Clock-out location',
            scheduled: scheduledAddress,
            actual: ls?.clock_out_address,
            actual_note: ls?.clock_out_location_mismatch ? `${Math.round(ls.clock_out_distance_feet)} ft from service address` : undefined,
            differs: Boolean(ls?.clock_out_address) && ls?.clock_out_address !== scheduledAddress,
            isLocation: true,
        },
    ]
}

const buildVisitWindow = ts => {
    if (!ts?.scheduled_start || !ts?.scheduled_end) {
        return null
    }
    const actualStart = ts?.actual_start ? isoTime(ts.actual_start) : null
    const actualEnd = ts?.actual_end ? isoTime(ts.actual_end) : null

    const scheduledStartMinutes = toMinutes(ts.scheduled_start)
    const scheduledEndMinutes = toMinutes(ts.scheduled_end)
    const actualStartMinutes = actualStart ? toMinutes(actualStart) : scheduledStartMinutes
    const actualEndMinutes = actualEnd ? toMinutes(actualEnd) : scheduledEndMinutes

    const earliest = Math.min(scheduledStartMinutes, actualStartMinutes)
    const latest = Math.max(scheduledEndMinutes, actualEndMinutes)
    const windowStart = Math.floor(earliest / 60) * 60 - 60
    const windowEnd = Math.ceil(latest / 60) * 60 + 60
    const span = windowEnd - windowStart

    const timeToPercent = minutes => Math.min(100, Math.max(0, ((minutes - windowStart) / span) * 100))

    const ticks = []
    for (let minutes = windowStart; minutes <= windowEnd; minutes += 60) {
        ticks.push(minutesToTime(minutes))
    }

    return {
        scheduled_start: timeToPercent(scheduledStartMinutes),
        scheduled_end: timeToPercent(scheduledEndMinutes),
        actual_start: timeToPercent(actualStartMinutes),
        actual_end: timeToPercent(actualEndMinutes),
        flagged: (Boolean(actualStart) && actualStart !== ts.scheduled_start) || (Boolean(actualEnd) && actualEnd !== ts.scheduled_end),
        ticks,
    }
}

const VISIT_ACTIONS = {
    review: {
        call: evvApi.bulkReviewVisits,
        title: n => `Clear ${n > 1 ? `${n} visits` : 'this visit'} for review?`,
        description: 'The visit will be queued and submitted to HHAX.',
        confirmLabel: n => (n > 1 ? `Review (${n})` : 'Review'),
        confirmColor: 'primary',
        successTitle: n => (n > 1 ? `${n} Visits Reviewed!` : 'Visit Reviewed!'),
        successDescription: 'Queued for submission to HHAX.',
    },
    resubmit: {
        call: evvApi.bulkResubmitVisits,
        title: n => `Resubmit ${n > 1 ? `${n} visits` : 'this visit'} to HHAX?`,
        description: 'This forces the visit past the unchanged-payload gate and resends it to HHAX.',
        confirmLabel: n => (n > 1 ? `Resubmit (${n})` : 'Resubmit'),
        confirmColor: 'error',
        successTitle: n => (n > 1 ? `${n} Visits Resubmitted!` : 'Visit Resubmitted!'),
        successDescription: 'Queued for resubmission to HHAX.',
    },
    escalate: {
        call: evvApi.bulkEscalateVisits,
        title: n => `Escalate ${n > 1 ? `${n} visits` : 'this visit'} to the payer?`,
        description: 'This records that a human is chasing it. It does not retry the submission or change the status.',
        confirmLabel: n => (n > 1 ? `Escalate (${n})` : 'Escalate'),
        confirmColor: 'error',
        successTitle: n => (n > 1 ? `${n} Visits Escalated!` : 'Visit Escalated!'),
        successDescription: 'Noted in the visit activity trail.',
        requiresNote: true,
    },
    cancel: {
        call: evvApi.bulkCancelVisits,
        title: n => `Cancel ${n > 1 ? `${n} visits` : 'this visit'}?`,
        description: 'If HHAX already accepted the visit this withdraws it; otherwise it simply stops here before ever reaching HHAX.',
        confirmLabel: n => (n > 1 ? `Cancel (${n})` : 'Cancel'),
        confirmColor: 'error',
        successTitle: n => (n > 1 ? `${n} Visits Cancelled!` : 'Visit Cancelled!'),
        successDescription: 'Removed from the EVV submission queue.',
    },
}

const VisitActionDialog = ({open, setOpen, action, loadData, items = []}) => {
    const {setLoading, setMessageBox} = useGlobalStates()
    const config = VISIT_ACTIONS[action]
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                note: config?.requiresNote ? yup.string().required('Please describe what you are escalating.') : yup.string(),
            }),
        ),
    })

    const submit = data => {
        setLoading(true)
        const visitIds = items.map(item => item?.visitId)
        config
            .call(config?.requiresNote ? {visitIds, note: data?.note} : {visitIds})
            .then(({data: {queued, failed}}) => {
                setLoading(false)
                setOpen(false)
                form.reset()
                if (failed > 0) {
                    setMessageBox({
                        status: 'error',
                        title: 'Some visits could not be updated',
                        description: `${queued} queued, ${failed} failed. Refresh to see which ones.`,
                    })
                } else {
                    setMessageBox({status: 'success', title: config.successTitle(items?.length), description: config.successDescription})
                }
                loadData()
            })
            .catch(error => {
                setLoading(false)
                setOpen(false)
                form.reset()
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
                loadData()
            })
    }

    if (!config) {
        return null
    }

    return (
        <Modal open={open}>
            <div className="action-container">
                <div className="action-content">
                    <Typography variant="h6" color="text.primary" style={{textAlign: 'center'}}>
                        {config.title(items?.length)}
                    </Typography>
                    <br />
                    <Typography variant="body2" color="text.secondary" style={{textAlign: 'center'}}>
                        {config.description}
                    </Typography>
                    {config.requiresNote && (
                        <>
                            <br />
                            <br />
                            <Typography variant="inputLabel" color="text.secondary">
                                Note
                            </Typography>
                            <TextField
                                placeholder="What are you escalating, and to whom?"
                                fullWidth
                                multiline
                                rows={4}
                                error={Boolean(form.formState.errors?.note)}
                                helperText={form.formState.errors?.note?.message}
                                {...form.register('note')}
                            />
                        </>
                    )}
                    <br />
                    <br />
                    <div className="action-buttons">
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => {
                                setOpen(false)
                                form.reset()
                            }}>
                            Go Back
                        </Button>
                        <Button variant="contained" color={config.confirmColor} fullWidth onClick={form.handleSubmit(submit)}>
                            {config.confirmLabel(items?.length)}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

const VisitCardSkeleton = () => (
    <SkeletonContainer className="card">
        <div className="card-profile">
            <div>
                <TextSkeleton width={110} fontSize={12} />
                <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 10}}>
                    <TextSkeleton width={130} fontSize={16} />
                    <TextSkeleton width={4} fontSize={14} />
                    <TextSkeleton width={150} fontSize={16} />
                </div>
            </div>
            <ChipSkeleton width={140} height={32} />
        </div>
        <br />
        <br />
        <div className="people-card">
            {[120, 110].map((width, index) => (
                <div className="person" key={index}>
                    <AvatarSkeleton size={40} />
                    <div className="person-label">
                        <TextSkeleton width={56} fontSize={12} />
                        <TextSkeleton width={width} fontSize={16} />
                    </div>
                </div>
            ))}
        </div>
        <br />
        <br />
        <div style={{display: 'flex', flexDirection: 'row'}}>
            {[96, 120, 70].map((width, index) => (
                <div key={index} style={{flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16}}>
                    <Skeleton variant="circular" width={24} height={24} />
                    <TextSkeleton width={width} />
                </div>
            ))}
        </div>
        <br />
        <br />
        <div className="row-bottons">
            {[0, 1, 2].map(index => (
                <ButtonSkeleton key={index} width="auto" sx={{flex: 1, minWidth: 160}} />
            ))}
        </div>
    </SkeletonContainer>
)

export default () => {
    const {mobile} = useMobile()
    const {visitId} = useParams()
    const {setLoading, setMessageBox} = useGlobalStates()
    const [visit, setVisit] = useState(null)
    const [attempts, setAttempts] = useState([])
    const [skeleton, setSkeleton] = useState(true)
    const [notFound, setNotFound] = useState(false)
    const [actionOpen, setActionOpen] = useState(null)
    const [visitInformation, setVisitInformation] = useState(null)
    const [activityLogs, setActivityLogs] = useState([])
    const [infoSkeleton, setInfoSkeleton] = useState(true)
    const [logsSkeleton, setLogsSkeleton] = useState(true)

    // refresh: the visit is already on screen (Refresh Status, or after an action),
    // so keep it visible behind the spinner instead of swapping in the skeleton.
    const loadData = (refresh = false) => {
        if (refresh) {
            setLoading(true)
        } else {
            setSkeleton(true)
        }
        evvApi
            .getVisit(visitId)
            .then(({data}) => {
                setVisit(data?.visit)
                setAttempts(data?.attempts || [])
                setSkeleton(false)
                if (refresh) setLoading(false)
            })
            .catch(error => {
                if (error?.response?.status === 404) {
                    setNotFound(true)
                } else {
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                }
                setSkeleton(false)
                if (refresh) setLoading(false)
            })
    }

    useEffect(() => {
        loadData()
    }, [visitId])

    useEffect(() => {
        if (visit?.branchId && visit?.scheduleId) {
            setInfoSkeleton(true)
            setLogsSkeleton(true)
            scheduleApi
                .getScheduleVisit(visit.branchId, visit.scheduleId, visitId)
                .then(({data}) => {
                    setVisitInformation(data)
                    setInfoSkeleton(false)
                })
                .catch(error => {
                    setInfoSkeleton(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })

            scheduleApi
                .getVisitActivityLogs(visit.branchId, visit.scheduleId, visitId, {page: 1, page_size: 100})
                .then(({data: {activity_logs}}) => {
                    setActivityLogs(activity_logs)
                    setLogsSkeleton(false)
                })
                .catch(error => {
                    setLogsSkeleton(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }, [visit?.branchId, visit?.scheduleId])

    const hasStepper = visit?.status && !NO_STEPPER_STATUSES.includes(visit?.status)
    const StepIcon = makeStepIcon(STATUS_TO_ERROR_STYLE[visit?.status])
    const latestAttempt = attempts?.length > 0 ? attempts[attempts.length - 1] : null
    const uniqueWarnings = [...new Set(visit?.warnings || [])]

    const header = mobile ? (
        <>
            <div className="header">
                <Link to="/evv">
                    <IconButton>
                        <ArrowBack />
                    </IconButton>
                </Link>
                <Typography variant="h6" color="text.primary">
                    Visit Details
                </Typography>
            </div>
            <br />
            <br />
            <br />
        </>
    ) : (
        <>
            <Typography variant="body1" color="text.secondary">
                <Link className="link" to="/evv">
                    EVV Management
                </Link>{' '}
                / Visit Details
            </Typography>
            <br />
            <div className="back-container">
                <Link className="back-button" to="/evv">
                    <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                </Link>
                <Typography variant="h5" color="text.primary">
                    Visit Details
                </Typography>
            </div>
            <br />
            <br />
        </>
    )

    const details = (
        <>
            <br />
            <br />
            <VisitWindow visitWindow={buildVisitWindow(visitInformation?.visit_time_summary)} skeleton={skeleton || infoSkeleton} />
            <br />
            <br />
            <ScheduleVsActual rows={buildScheduleVsActual(visitInformation)} skeleton={skeleton || infoSkeleton} />
            <br />
            <br />
            <div className="detail-grid">
                <div className="detail-grid-left">
                    <VisitInformation visit={visitInformation} skeleton={skeleton || infoSkeleton} />
                </div>
                <div className="detail-grid-right">
                    <ActivityLog items={activityLogs} clientName={visit?.client?.name} skeleton={skeleton || logsSkeleton} />
                </div>
            </div>
        </>
    )

    if (skeleton) {
        return (
            <>
                {header}
                <VisitCardSkeleton />
                {details}
            </>
        )
    }

    if (notFound || !visit) {
        return (
            <>
                <Link className="link" to="/evv">
                    <ArrowBack fontSize="small" /> Back to EVV Management
                </Link>
                <br />
                <br />
                <Typography variant="h6" color="text.primary">
                    Visit not found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    This visit does not exist, or it belongs to a different organization.
                </Typography>
            </>
        )
    }

    return (
        <>
            {header}
            <div className="card">
                <div className="card-profile">
                    <div>
                        <Typography variant="h9" color="text.secondary">
                            {visit?.service?.toUpperCase()}
                        </Typography>
                        <div className="content" style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 10}}>
                            <Typography variant="h7" color="text.primary">
                                {format(new Date(visit?.date), 'EEE, dd MMM yyyy')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                |
                            </Typography>
                            <Typography variant="h7" color="text.primary">
                                {formatTime(isoTime(visit?.scheduled?.start))} - {formatTime(isoTime(visit?.scheduled?.end))}
                            </Typography>
                        </div>
                    </div>
                    {statusChip(visit?.status, visit?.queued)}
                </div>
                <br />
                <br />
                <div className="people-card">
                    <div className="person">
                        {visit?.client?.avatar_url ? (
                            <Avatar src={visit.client.avatar_url} style={{width: 40, height: 40}} />
                        ) : (
                            <Avatar
                                style={{
                                    backgroundColor: getColorFromName(visit?.client?.name),
                                    width: 40,
                                    height: 40,
                                    fontSize: 20,
                                }}>
                                {getInitials(visit?.client?.name)}
                            </Avatar>
                        )}
                        <div className="person-label">
                            <Typography variant="h10" color="text.secondary">
                                Client
                            </Typography>
                            <Typography variant="h8" color="text.primary">
                                {visit?.client?.name}
                            </Typography>
                        </div>
                    </div>
                    <div className="person">
                        {visit?.caregiver?.avatar_url ? (
                            <Avatar src={visit.caregiver.avatar_url} style={{width: 40, height: 40}} />
                        ) : (
                            <Avatar
                                style={{
                                    backgroundColor: getColorFromName(visit?.caregiver?.name),
                                    width: 40,
                                    height: 40,
                                    fontSize: 20,
                                }}>
                                {getInitials(visit?.caregiver?.name)}
                            </Avatar>
                        )}
                        <div className="person-label">
                            <Typography variant="h10" color="text.secondary">
                                Caregiver
                            </Typography>
                            <Typography variant="h8" color="text.primary">
                                {visit?.caregiver?.name}
                            </Typography>
                        </div>
                    </div>
                </div>
                <br />
                <br />
                {hasStepper && (
                    <>
                        <Stepper activeStep={STATUS_TO_STEP[visit?.status] ?? 0} alternativeLabel style={{width: '100%'}}>
                            {STEPS.map((label, index) => (
                                <Step key={label}>
                                    <StepLabel
                                        error={index === STATUS_TO_ERROR_STEP[visit?.status]}
                                        StepIconComponent={StepIcon}
                                        sx={{'&.Mui-error .MuiStepLabel-label': {color: STATUS_TO_ERROR_STYLE[visit?.status]?.color}}}>
                                        {label}
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        <br />
                        <br />
                    </>
                )}
                {visit?.status === 'pending_review' && uniqueWarnings.length > 0 && (
                    <>
                        <div className="blocking-card">
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                                <Warning color="warning" fontSize="small" />
                                <Typography variant="h9" color="warning.main">
                                    {uniqueWarnings.length} item{uniqueWarnings.length > 1 ? 's' : ''} block submission
                                </Typography>
                            </div>
                            {uniqueWarnings.map((code, index) => {
                                const detail = WARNING_DETAILS[code]
                                return (
                                    <div className="blocking-item" key={code} style={{borderTop: index > 0 ? '1px solid #ed6c0229' : 'none'}}>
                                        <div>
                                            <Typography variant="body2" color="text.primary">
                                                {detail?.title || code}
                                            </Typography>
                                            {detail && infoSkeleton ? (
                                                <SkeletonContainer>
                                                    <TextSkeleton width={320} fontSize={12} />
                                                </SkeletonContainer>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    {detail
                                                        ? detail.description(
                                                              visitInformation?.visit_time_summary,
                                                              visitInformation?.visit_location_summary,
                                                          )
                                                        : ''}
                                                </Typography>
                                            )}
                                        </div>
                                        <Button variant="outlined" size="small">
                                            {detail?.actionLabel || 'Review'}
                                        </Button>
                                    </div>
                                )
                            })}
                        </div>
                        <br />
                    </>
                )}
                {visit?.status === 'rejected' && (
                    <>
                        <div className="blocking-card" style={{backgroundColor: '#d32f2f14'}}>
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                                <Close color="error" fontSize="small" />
                                <Typography variant="h9" color="error.main">
                                    Rejected by HHAX
                                </Typography>
                            </div>
                            <Typography variant="body2" color="text.secondary" style={{marginTop: 14}}>
                                {visit?.hhax?.lastError}
                            </Typography>
                            <div style={{display: 'flex', flexDirection: 'row', gap: 24, marginTop: 14}}>
                                <Typography variant="body2" color="text.primary" style={{fontWeight: 600}}>
                                    Error code:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {visit?.hhax?.errorCodes?.join(', ') || '-'}
                                </Typography>
                            </div>
                            <div style={{display: 'flex', flexDirection: 'row', gap: 24, marginTop: 8}}>
                                <Typography variant="body2" color="text.primary" style={{fontWeight: 600}}>
                                    Transaction ID:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {latestAttempt?.transactionId || '-'}
                                </Typography>
                            </div>
                            {latestAttempt?.resolvedAt && (
                                <div style={{display: 'flex', flexDirection: 'row', gap: 24, marginTop: 8}}>
                                    <Typography variant="body2" color="text.primary" style={{fontWeight: 600}}>
                                        Rejected at:
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {format(new Date(latestAttempt.resolvedAt), 'MMM d, yyyy, h:mm a')}
                                    </Typography>
                                </div>
                            )}
                        </div>
                        <br />
                    </>
                )}
                {visit?.status === 'cancelled' && (
                    <>
                        <div className="blocking-card" style={{backgroundColor: '#d32f2f14'}}>
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                                <Close color="error" fontSize="small" />
                                <Typography variant="h9" color="error.main">
                                    Cancelled before submission
                                </Typography>
                            </div>
                            <Typography variant="body2" color="text.secondary" style={{marginTop: 14}}>
                                {visit?.statusReason ||
                                    "This visit was cancelled before it reached HHAX — it was never submitted and won't be billed."}
                            </Typography>
                        </div>
                        <br />
                    </>
                )}
                {visit?.status === 'blocked' && (
                    <>
                        <div className="blocking-card" style={{backgroundColor: '#7b1fa214'}}>
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                                <RemoveCircle style={{color: '#7B1FA2'}} fontSize="small" />
                                <Typography variant="h9" style={{color: '#7B1FA2'}}>
                                    Blocked — someone outside Careviah must act
                                </Typography>
                            </div>
                            <Typography variant="body2" color="text.secondary" style={{marginTop: 14}}>
                                {visit?.statusReason || visit?.hhax?.lastError}
                            </Typography>
                            <div style={{display: 'flex', flexDirection: 'row', gap: 24, marginTop: 14}}>
                                <Typography variant="body2" color="text.primary" style={{fontWeight: 600}}>
                                    Error code:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {visit?.hhax?.errorCodes?.join(', ') || '-'}
                                </Typography>
                            </div>
                            <div style={{display: 'flex', flexDirection: 'row', gap: 24, marginTop: 8}}>
                                <Typography variant="body2" color="text.primary" style={{fontWeight: 600}}>
                                    Transaction ID:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {latestAttempt?.transactionId || '-'}
                                </Typography>
                            </div>
                        </div>
                        <br />
                    </>
                )}
                {visit?.status === 'submitted' && (
                    <>
                        <div className="blocking-card" style={{backgroundColor: '#2e7d3214'}}>
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                                <Timelapse color="success" fontSize="small" />
                                <Typography variant="h9" color="success.main">
                                    Sent to HHAX, awaiting response
                                </Typography>
                            </div>
                            <Typography variant="body2" color="text.secondary" style={{marginTop: 14}}>
                                HHAX typically responds within seconds. This page refreshes with the verdict on your next visit.
                            </Typography>
                            {latestAttempt?.sentAt && (
                                <div style={{display: 'flex', flexDirection: 'row', gap: 6, marginTop: 14}}>
                                    <Typography variant="body2" color="text.primary" style={{fontWeight: 600}}>
                                        Sent at:
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {format(new Date(latestAttempt.sentAt), 'MMM d, yyyy, h:mm a')}
                                    </Typography>
                                </div>
                            )}
                        </div>
                        <br />
                    </>
                )}
                {visit?.status === 'approved' && (
                    <>
                        <div className="blocking-card" style={{backgroundColor: '#2e7d3214'}}>
                            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                                <Check color="success" fontSize="small" />
                                <Typography variant="h9" color="success.main">
                                    Approved by HHAX
                                </Typography>
                            </div>
                            <Typography variant="body2" color="text.secondary" style={{marginTop: 14}}>
                                This visit has been accepted by HHAX.
                            </Typography>
                            <div style={{display: 'flex', flexDirection: 'row', gap: 6, marginTop: 14}}>
                                <Typography variant="body2" color="text.primary" style={{fontWeight: 600}}>
                                    HHAX visit ID:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {visit?.hhax?.externalVisitId || '-'}
                                </Typography>
                            </div>
                            <div style={{display: 'flex', flexDirection: 'row', gap: 6, marginTop: 8}}>
                                <Typography variant="body2" color="text.primary" style={{fontWeight: 600}}>
                                    Transaction ID:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {latestAttempt?.transactionId || '-'}
                                </Typography>
                            </div>
                        </div>
                        <br />
                    </>
                )}
                {visit?.status === 'not_required' && (
                    <>
                        <div className="blocking-card" style={{backgroundColor: '#0000000a'}}>
                            <Typography variant="body2" color="text.secondary">
                                {visit?.statusReason || 'EVV is not required for this visit. No submission action is needed.'}
                            </Typography>
                        </div>
                        <br />
                    </>
                )}
                {visit?.status === 'withdrawn' && (
                    <>
                        <div className="blocking-card" style={{backgroundColor: '#0000000a'}}>
                            <Typography variant="body2" color="text.secondary">
                                {visit?.statusReason || 'This visit was accepted by HHAX and then deleted, and will not be resubmitted.'}
                            </Typography>
                        </div>
                        <br />
                    </>
                )}
                {visit?.status === 'pending_review' && (
                    <div className="row-bottons">
                        <Button variant="outlined" className="buttons">
                            Generate PDF Report
                        </Button>
                        <Button variant="outlined" color="error" className="buttons" onClick={() => setActionOpen('cancel')}>
                            Cancel Visit
                        </Button>
                        <Button variant="contained" className="buttons" onClick={() => setActionOpen('review')}>
                            Submit to HHAX
                        </Button>
                    </div>
                )}
                {visit?.status === 'submitted' && (
                    <div className="row-bottons">
                        <Button variant="outlined" className="buttons">
                            Generate PDF Report
                        </Button>
                        <Button variant="outlined" className="buttons" onClick={() => loadData(true)}>
                            Refresh Status
                        </Button>
                    </div>
                )}
                {visit?.status === 'approved' && (
                    <div className="row-bottons">
                        <Button variant="outlined" className="buttons">
                            Generate PDF Report
                        </Button>
                        <Button variant="contained" className="buttons">
                            Request Claim
                        </Button>
                    </div>
                )}
                {visit?.status === 'rejected' && (
                    <div className="row-bottons">
                        <Button variant="outlined" className="buttons">
                            Generate PDF Report
                        </Button>
                        <Button variant="contained" color="error" className="buttons" onClick={() => setActionOpen('resubmit')}>
                            Fix and Resubmit
                        </Button>
                    </div>
                )}
                {visit?.status === 'cancelled' && (
                    <div className="row-bottons">
                        <Button variant="outlined" className="buttons">
                            Generate PDF Report
                        </Button>
                        <Button variant="contained" color="error" className="buttons" onClick={() => setActionOpen('resubmit')}>
                            Resubmit
                        </Button>
                    </div>
                )}
                {visit?.status === 'blocked' && (
                    <div className="row-bottons">
                        <Button variant="outlined" className="buttons">
                            Generate PDF Report
                        </Button>
                        <Button variant="contained" color="error" className="buttons" onClick={() => setActionOpen('escalate')}>
                            Mark Escalated
                        </Button>
                    </div>
                )}
            </div>
            {hasStepper && details}
            <br />
            <br />
            <br />
            <br />
            <VisitActionDialog
                open={Boolean(actionOpen)}
                setOpen={value => {
                    if (!value) setActionOpen(null)
                }}
                action={actionOpen}
                loadData={() => loadData(true)}
                items={visit ? [visit] : []}
            />
        </>
    )
}
